/**
 * Currency utility functions for handling decimal-based currency values
 * where 1 = 0.01 (i.e., 4650 = 46.50)
 */

/**
 * Converts a decimal currency value to actual currency amount
 * @param decimalAmount - The decimal amount (e.g., 4650)
 * @returns The actual currency amount (e.g., 46.50)
 */
export function convertDecimalToCurrency(decimalAmount: number): number {
  return decimalAmount / 100;
}

/**
 * Formats a decimal currency value to a formatted currency string
 * @param decimalAmount - The decimal amount (e.g., 4650)
 * @param currency - The currency symbol (default: '₹')
 * @param locale - The locale for formatting (default: 'en-IN')
 * @returns Formatted currency string (e.g., '₹46.50')
 */
export function formatDecimalCurrency(
  decimalAmount: number,
  currency: string = "₹",
  locale: string = "en-IN",
): string {
  const actualAmount = convertDecimalToCurrency(decimalAmount);
  return `${currency}${actualAmount.toLocaleString(locale, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
}

/**
 * Quick currency formatter for use in components
 * @param decimalAmount - The decimal amount (e.g., 4650)
 * @returns Formatted currency string (e.g., '₹46.50')
 */
export const formatCurrency = (decimalAmount: number): string => {
  return formatDecimalCurrency(decimalAmount);
};

/**
 * Converts actual currency amount to decimal format
 * @param actualAmount - The actual currency amount (e.g., 46.50)
 * @returns The decimal amount (e.g., 4650)
 */
export function convertCurrencyToDecimal(actualAmount: number): number {
  return Math.round(actualAmount * 100);
}
